<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <p class="text-sm text-gray-600">
        {{ $t("ticketPurchase.services.addOptionalServices") }}
      </p>
      <button class="text-xs text-pink-500 underline" @click="$emit('skip')">
        {{ $t("ticketPurchase.services.skip") }}
      </button>
    </div>

    <div
      v-if="serviceOptions.length && ticketEntries.length"
      class="max-h-[52dvh] space-y-5 overflow-y-auto pr-1 md:max-h-[46vh]"
    >
      <div
        v-for="(entry, entryIdx) in ticketEntries"
        :key="`${entry.ticketTypeId}-${entry.ticketIndex}`"
        class="overflow-hidden rounded-xl border"
      >
        <!-- Ticket header -->
        <button
          class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left hover:bg-gray-100"
          @click="toggleSection(entryIdx)"
        >
          <span class="text-sm font-semibold text-[#2e234b]">
            {{ entry.ticketTypeName }} #{{ entry.ticketIndex + 1 }}
          </span>
          <svg
            class="h-4 w-4 text-gray-500 transition-transform"
            :class="{ 'rotate-180': openSections[entryIdx] }"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <!-- Service options for this ticket -->
        <div v-if="openSections[entryIdx]" class="space-y-3 p-4">
          <div
            v-for="svc in serviceOptions"
            :key="serviceId(svc)"
            class="rounded-lg border p-3 hover:border-pink-500"
            :class="{ 'opacity-50': isSoldOut(svc) }"
          >
            <div
              class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
            >
              <div class="min-w-0 flex-1">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="text-sm font-semibold text-[#2e234b]">
                      {{ svc.name }}
                    </div>
                    <div class="text-xs text-gray-500">
                      {{
                        svc.description ??
                        $t("ticketPurchase.services.optionalAddOn")
                      }}
                    </div>
                  </div>
                  <div
                    class="whitespace-nowrap text-sm font-bold text-[#2e234b]"
                  >
                    {{ formatPrice(svc.price ?? 0, svc.currency ?? "EUR") }}
                  </div>
                </div>

                <div
                  v-if="isSoldOut(svc)"
                  class="mt-2 text-xs font-semibold text-red-500"
                >
                  {{ $t("ticketPurchase.tickets.soldOut") }}
                </div>
                <div
                  v-else-if="(svc.maxQuantity ?? 0) > 0"
                  class="mt-2 text-xs text-gray-400"
                >
                  {{
                    $t("ticketPurchase.tickets.left", { count: remaining(svc) })
                  }}
                </div>
              </div>

              <div class="flex items-center justify-end gap-2 sm:pl-3">
                <button
                  class="h-9 w-9 rounded-full border text-sm text-[#2e234b] disabled:opacity-40"
                  :aria-label="`Decrease ${svc.name} count`"
                  :disabled="(entry.serviceCounts[serviceId(svc)] ?? 0) === 0"
                  @click="decrement(entryIdx, svc)"
                >
                  −
                </button>
                <span
                  class="min-w-[24px] text-center text-sm font-semibold text-[#2e234b]"
                >
                  {{ entry.serviceCounts[serviceId(svc)] ?? 0 }}
                </span>
                <button
                  class="h-9 w-9 rounded-full border bg-pink-500 text-sm text-white"
                  :aria-label="`Increase ${svc.name} count`"
                  :disabled="isAtCapacity(svc)"
                  :class="{
                    'cursor-not-allowed opacity-40': isAtCapacity(svc),
                  }"
                  @click="increment(entryIdx, svc)"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!serviceOptions.length" class="text-sm text-gray-500">
      {{ $t("ticketPurchase.services.noServiceOptions") }}
    </div>
    <div v-else class="text-sm text-gray-500">
      {{ $t("ticketPurchase.services.selectTicketsFirst") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { formatPrice } from "~~/shared/utils/formatPrice";
import { availableQuantity, isUnavailable } from "./availability";
import type { TicketEntry } from "./types";

type ServiceOptionLike = {
  id?: string | number;
  name?: string;
  description?: string;
  price?: number;
  currency?: string;
  maxQuantity?: number;
  sold?: number;
  held?: number;
  available?: number | null;
};

const props = defineProps<{
  ticketEntries: TicketEntry[];
  serviceOptions: ServiceOptionLike[];
}>();

const emit = defineEmits<{
  (e: "skip"): void;
  (e: "update:ticketEntries", value: TicketEntry[]): void;
}>();

const serviceId = (s: ServiceOptionLike) => String(s.id ?? s.name ?? "service");

// Track which ticket sections are expanded (all open by default)
const openSections = ref<Record<number, boolean>>({});
function toggleSection(idx: number) {
  openSections.value[idx] = !(openSections.value[idx] ?? true);
}

watch(
  () => props.ticketEntries.length,
  () => {
    for (let i = 0; i < props.ticketEntries.length; i++) {
      if (openSections.value[i] === undefined) openSections.value[i] = true;
    }
  },
  { immediate: true },
);

function updateEntry(entryIdx: number, patch: Partial<TicketEntry>) {
  const updated = [...props.ticketEntries];
  const existing = updated[entryIdx];
  if (!existing) return;
  updated[entryIdx] = { ...existing, ...patch };
  emit("update:ticketEntries", updated);
}

function totalCountForService(svc: ServiceOptionLike): number {
  const id = serviceId(svc);
  return props.ticketEntries.reduce(
    (sum, entry) => sum + (entry.serviceCounts[id] ?? 0),
    0,
  );
}

function remaining(svc: ServiceOptionLike): number {
  const available = availableQuantity(svc);
  return available === null
    ? Infinity
    : Math.max(0, available - totalCountForService(svc));
}

function isSoldOut(svc: ServiceOptionLike): boolean {
  return isUnavailable(svc);
}

function isAtCapacity(svc: ServiceOptionLike): boolean {
  if (isSoldOut(svc)) return true;
  if (!svc.maxQuantity || svc.maxQuantity <= 0) return false;
  return remaining(svc) <= 0;
}

function increment(entryIdx: number, service: ServiceOptionLike) {
  const entry = props.ticketEntries[entryIdx];
  if (!entry) return;
  if (isAtCapacity(service)) return;
  const id = serviceId(service);
  const current = entry.serviceCounts;
  updateEntry(entryIdx, {
    serviceCounts: { ...current, [id]: (current[id] ?? 0) + 1 },
  });
}

function decrement(entryIdx: number, service: ServiceOptionLike) {
  const entry = props.ticketEntries[entryIdx];
  if (!entry) return;
  const id = serviceId(service);
  const current = entry.serviceCounts;
  updateEntry(entryIdx, {
    serviceCounts: { ...current, [id]: Math.max((current[id] ?? 0) - 1, 0) },
  });
}
</script>
